<template>
  <div class="pay-container">
    <div v-if="isLoading" class="google-pay-loader"></div>

    <div class="gpay-wrapper">
      <google-pay-button
        v-if="paymentRequest"
        ref="googlePayBtn"
        :environment="environment"
        button-type="plain"
        button-color="black"
         button-size-mode="fill"
        :paymentRequest="paymentRequest"
        @loadpaymentdata="onLoadPaymentData"
        @error="onError"
      />
    </div>
  </div>
</template>

<script>
import '@google-pay/button-element';
import braintree from 'braintree-web';

export default {
  name: 'GooglePay',
  props: {
    currentSelectedPlan: {
      type: Object,
      default: () => ({ price: 0 })
    },
    sendNonce: {
      type: Function,
      default: () => {}
    },
    clientToken: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      googlePayInstance: null,
      paymentRequest: null,
      isLoading: true,
      environment: process.env.VUE_APP_BRAINTREE_ENV.toUpperCase(),
    };
  },
  watch: {
    async clientToken(newToken) {
      if (newToken) {
        await this.initializeGooglePay(newToken);
      }
    }
  },
  methods: {
    async initializeGooglePay(token) {
      this.isLoading = true;
      try {
        const clientInstance = await braintree.client.create({
          authorization: token
        });

        const googlePaymentInstance = await braintree.googlePayment.create({
          client: clientInstance,
          googlePayVersion: 2,
          googleMerchantId:
            this.environment === 'PRODUCTION'
              ? process.env.VUE_APP_GOOGLEPAY_MERCHANT_ID
              : undefined
        });

        const paymentsClient = new google.payments.api.PaymentsClient({
          environment: this.environment,
        });

        const isReady = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods
        });

        if (!isReady.result) {
          console.warn('Google Pay is not available');
          this.isLoading = false;
          return;
        }

        const dataCollectorInstance = await braintree.dataCollector.create({
          client: clientInstance,
          kount: true // Required for deviceData
        });

        this.deviceData = dataCollectorInstance.deviceData;

        const paymentRequest = googlePaymentInstance.createPaymentDataRequest({
          transactionInfo: {
            currencyCode: 'USD',
            totalPriceStatus: 'FINAL',
            totalPrice: this.calculateTotalPrice()
          },
          environment: this.environment !== 'PRODUCTION' ? "TEST" : this.environment,
          merchantInfo: {
            merchantName: process.env.VUE_APP_GOOGLEPAY_MERCHANT_NAME,
            ...(this.environment === 'PRODUCTION' && { merchantId: process.env.VUE_APP_GOOGLEPAY_MERCHANT_ID })
          },
          shippingAddressRequired: false
        });

        // Add required card networks & auth methods
        const cardMethod = paymentRequest.allowedPaymentMethods[0];
        cardMethod.parameters.allowedCardNetworks = ['VISA', 'MASTERCARD', 'AMEX'];
        cardMethod.parameters.allowedAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
        // cardMethod.parameters.billingAddressRequired = true;
        cardMethod.parameters.billingAddressParameters = {
          format: 'FULL',
          // phoneNumberRequired: true
        };

        // Set the paymentRequest in the component's data
        this.paymentRequest = paymentRequest;

        // Now, set it to the google-pay-button element after it's rendered
        this.$nextTick(() => {
          if (this.$refs.googlePayBtn) {
            this.$refs.googlePayBtn.paymentRequest = paymentRequest;
          }
        });
        this.googlePayInstance = googlePaymentInstance;

      } catch (err) {
        console.error('Error initializing Google Pay:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async onLoadPaymentData(event) {
      try {
        const payload = await this.googlePayInstance.parseResponse(event.detail);

        const nonce = payload.nonce;

        this.sendNonce(nonce, '', this.deviceData);
      } catch (error) {
        console.error('Error processing Google Pay data:', error);
      }
    },

    onError(event) {
      console.error('Google Pay Error:', event.detail);
    },

    calculateTotalPrice() {
      const price = this.currentSelectedPlan?.price || 0;
      return price.toFixed(2);
    }
  }
};
</script>

<style scoped>
.pay-container {
  display: flex;
  width: 100%;
  justify-content: center;
}
.gpay-wrapper {
  width: 100%;
}

/* Force google-pay-button element full width */
google-pay-button {
  width: 100% !important;
  display: block;
}

/* Force internal shadow DOM iframe full width (Chrome fix) */
::v-deep google-pay-button > div,
::v-deep google-pay-button iframe {
  width: 100% !important;
  min-width: 100% !important;
  display: block !important;
  max-width: 100% !important;
}

/* Force the actual rendered GPay button */
::v-deep .gpay-button {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}


/* Make the outer button full width */
::v-deep button.gpay-card-info-container {
  width: 100% !important;
  min-width: 100% !important;
  display: block !important;
}

/* Force iframe full width */
::v-deep .gpay-card-info-iframe {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

/* Force internal static button container full width */
::v-deep .gpay-button-container-size-match-height {
  width: 100% !important;
  min-width: 100% !important;
}

/* Force the actual visible GPay button full width */
::v-deep .gpay-button {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  display: block !important;
}
::v-deep .gpay-button,
::v-deep .gpay-button-container-size-match-height {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}
</style>